import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import type { DashboardLayoutMap, DashboardPreset } from "@/lib/dashboardLayout";

export type ThemeMode = "light" | "dark";
export type ThemeId =
  | "bold-dark"
  | "default"
  | "serenity"
  | "forest"
  | "ocean"
  | "dawn"
  | "mono"
  | "wellness-green"
  | "social-stories"
  | "native-mobile"
  | "forgerestore";
export type FontFamilyId = "inter" | "merriweather" | "plex" | "manrope";
export type Density = "compact" | "comfortable" | "spacious";

export interface AppearancePrefs {
  themeMode: ThemeMode;
  themeId: ThemeId;
  fontFamilyId: FontFamilyId;
  density: Density;
  reducedMotion: boolean;
  /** In-app text size multiplier. Range 0.8–2.0. Restored in Task #79. */
  fontScale: number;
  dashboardLayout: DashboardLayoutMap;
  /** Preset is per-role so sponsor and sponsee dashboards can pick independently. */
  dashboardPreset: { sponsee?: DashboardPreset; sponsor?: DashboardPreset };
}

export const FONT_SCALE_MIN = 0.8;
export const FONT_SCALE_MAX = 2.0;
export const FONT_SCALE_STEP = 0.05;

// Default flipped to the new bold-dark identity (matches the dashboard +
// community-portal mockups). The original "Recovery" purple palette is still
// available as a selectable theme — just no longer the default. The bold-dark
// theme is intrinsically dark, so we also default themeMode to "dark".
// Task #189: primary typeface is now Manrope (the ForgeRestore brand font).
// The new "forgerestore" theme is available in the picker but is NOT the
// default — bold-dark remains the first-paint identity until a follow-up
// task flips it explicitly.
export const DEFAULT_PREFS: AppearancePrefs = {
  themeMode: "dark",
  themeId: "bold-dark",
  fontFamilyId: "manrope",
  density: "comfortable",
  reducedMotion: false,
  fontScale: 1,
  dashboardLayout: {},
  dashboardPreset: {},
};

function clampFontScale(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, n));
}

const STORAGE_KEY = "recoverme-appearance-v1";

// Task #134 — curated picker: the user-facing Appearance settings show
// exactly these four themes (Bold Dark + the three Community Portal
// palettes). The legacy themes (default/serenity/forest/ocean/dawn/mono)
// remain valid `ThemeId` values and still have CSS blocks so any user
// preference saved before this task continues to render correctly — we
// just no longer offer them as picker choices.
export const THEME_OPTIONS: {
  id: ThemeId;
  name: string;
  description: string;
  swatch: { primary: string; bg: string; accent: string };
}[] = [
  {
    id: "bold-dark",
    name: "Bold Dark",
    description: "Deep black + amber — the ForgeRestore signature",
    swatch: { primary: "#FBBF24", bg: "#0A0A0B", accent: "#1A1A1F" },
  },
  {
    id: "wellness-green",
    name: "Wellness Green",
    description: "Leaf green + soft sky — calm and grounded",
    swatch: { primary: "#3A8A55", bg: "#F3F7F1", accent: "#EAF2EA" },
  },
  {
    id: "social-stories",
    name: "Social Stories",
    description: "Warm cream + terracotta — friendly and editorial",
    swatch: { primary: "#E05D4E", bg: "#FDFBF7", accent: "#F0F2FF" },
  },
  {
    id: "native-mobile",
    name: "Native Mobile",
    description: "Cool slate + amber — crisp app-like feel",
    swatch: { primary: "#F59E0B", bg: "#F8FAFC", accent: "#E2E8F0" },
  },
  // Task #189 — new ForgeRestore brand palette. Listed last (not default).
  // Deep Forge Blue + Sunrise Ember on a Slate Gray surface.
  {
    id: "forgerestore",
    name: "ForgeRestore",
    description: "Deep Forge Blue + Sunrise Ember — the brand palette",
    swatch: { primary: "#0A4D68", bg: "#E0E6ED", accent: "#FFB84C" },
  },
];

export const FONT_OPTIONS: { id: FontFamilyId; name: string; description: string; cssFamily: string }[] = [
  // Task #189 — Manrope is the primary ForgeRestore typeface and the new default.
  {
    id: "manrope",
    name: "Manrope",
    description: "ForgeRestore primary — clean, modern, geometric",
    cssFamily: 'Manrope, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    id: "inter",
    name: "Inter",
    description: "Modern, neutral, easy on the eyes",
    cssFamily: '"Inter Variable", Inter, system-ui, sans-serif',
  },
  {
    id: "merriweather",
    name: "Merriweather",
    description: "Warm serif, great for long reading",
    cssFamily: "Merriweather, Georgia, serif",
  },
  {
    id: "plex",
    name: "IBM Plex Sans",
    description: "Crisp, slightly humanist",
    cssFamily: '"IBM Plex Sans", system-ui, sans-serif',
  },
];

function readLocal(): Partial<AppearancePrefs> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    // pageZoom was dropped in Task #75 and not restored; strip silently.
    if ("pageZoom" in parsed) {
      delete parsed.pageZoom;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      } catch {
        /* ignore */
      }
    }
    if ("fontScale" in parsed) {
      parsed.fontScale = clampFontScale(parsed.fontScale);
    }
    return parsed;
  } catch {
    return {};
  }
}

function writeLocal(prefs: AppearancePrefs) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota exhausted, ignore */
  }
}

function applyToDom(prefs: AppearancePrefs) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.classList.toggle("dark", prefs.themeMode === "dark");
  html.setAttribute("data-theme", prefs.themeId);
  html.setAttribute("data-font", prefs.fontFamilyId);
  html.setAttribute("data-density", prefs.density);
  html.setAttribute("data-reduce-motion", prefs.reducedMotion ? "true" : "false");
  html.style.setProperty("--font-scale", String(clampFontScale(prefs.fontScale)));
}

interface AppearanceContextValue {
  prefs: AppearancePrefs;
  setPrefs: (patch: Partial<AppearancePrefs>) => void;
  reset: () => void;
  isReducedMotion: boolean; // resolves OS pref OR user pref
  isPersistingToServer: boolean;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const lastToastAt = useRef<number>(0);
  const userInitiatedSaveRef = useRef(false);

  const [prefs, setPrefsState] = useState<AppearancePrefs>(() => ({
    ...DEFAULT_PREFS,
    ...readLocal(),
  }));

  const [osReducedMotion, setOsReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  // Watch OS reduce-motion changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setOsReducedMotion(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Apply to DOM and localStorage on every change
  useEffect(() => {
    applyToDom(prefs);
    writeLocal(prefs);
  }, [prefs]);

  // Server-side prefs (only when authenticated)
  const { data: serverPrefs } = useQuery<AppearancePrefs>({
    queryKey: ["/api/user/preferences"],
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // First time the server prefs load, replace local state with server values
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!serverPrefs || hydratedRef.current) return;
    hydratedRef.current = true;
    setPrefsState((prev) => ({ ...prev, ...sanitizeServer(serverPrefs) }));
  }, [serverPrefs]);

  // Reset hydration flag when auth state flips so re-login refetches
  useEffect(() => {
    if (!isAuthenticated) hydratedRef.current = false;
  }, [isAuthenticated]);

  const persistMutation = useMutation({
    mutationFn: (patch: Partial<AppearancePrefs>) =>
      apiRequest("PATCH", "/api/user/preferences", patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
      if (!userInitiatedSaveRef.current) return;
      userInitiatedSaveRef.current = false;
      const now = Date.now();
      if (now - lastToastAt.current < 4000) return;
      lastToastAt.current = now;
      toast({
        title: "Appearance saved",
        description: "Your preferences are synced across devices.",
        duration: 1800,
      });
    },
    onError: () => {
      toast({
        title: "Couldn't save appearance",
        description: "Your changes are kept locally and will retry on the next change.",
        variant: "destructive",
        duration: 3000,
      });
    },
  });

  // Debounced server persistence
  const pendingPatch = useRef<Partial<AppearancePrefs>>({});
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (!isAuthenticated) {
      pendingPatch.current = {};
      return;
    }
    const patch = pendingPatch.current;
    pendingPatch.current = {};
    if (Object.keys(patch).length === 0) return;
    persistMutation.mutate(patch);
  }, [isAuthenticated, persistMutation]);

  const setPrefs = useCallback(
    (patch: Partial<AppearancePrefs>) => {
      const safePatch: Partial<AppearancePrefs> =
        patch.fontScale !== undefined
          ? { ...patch, fontScale: clampFontScale(patch.fontScale) }
          : patch;
      setPrefsState((prev) => ({ ...prev, ...safePatch }));
      pendingPatch.current = { ...pendingPatch.current, ...safePatch };
      userInitiatedSaveRef.current = true;
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(flush, 400);
    },
    [flush],
  );

  const reset = useCallback(() => {
    setPrefs(DEFAULT_PREFS);
  }, [setPrefs]);

  const value = useMemo<AppearanceContextValue>(
    () => ({
      prefs,
      setPrefs,
      reset,
      isReducedMotion: osReducedMotion || prefs.reducedMotion,
      isPersistingToServer: persistMutation.isPending,
    }),
    [prefs, setPrefs, reset, osReducedMotion, persistMutation.isPending],
  );

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
}

function sanitizeServer(serverPrefs: Partial<AppearancePrefs>): AppearancePrefs {
  const merged = { ...DEFAULT_PREFS, ...serverPrefs };
  // Migrate older string-shaped dashboardPreset to per-role map shape.
  const dp = merged.dashboardPreset as unknown;
  if (typeof dp === "string") {
    merged.dashboardPreset = { sponsee: dp as DashboardPreset, sponsor: dp as DashboardPreset };
  } else if (!dp || typeof dp !== "object") {
    merged.dashboardPreset = {};
  }
  merged.fontScale = clampFontScale(merged.fontScale);
  return merged;
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error("useAppearance must be used inside <AppearanceProvider>");
  }
  return ctx;
}
